/**
 * kit-studio.js
 * -----------------------------------------------------------------------------
 * Entry point. Wires the store to the 3D stage, the print surface, the UI and
 * the cart, then gets out of the way.
 *
 * Everything below is subscription plumbing: the store is the only thing that
 * changes, and each subsystem reacts to the keys it cares about. Nothing here
 * decides what a product is or how it looks. That's `kit-catalog.js`.
 *
 * Runtime settings arrive on `window.KitStudioSettings`, injected by the Liquid
 * section. With no settings present the module still boots in preview mode.
 */

import { buildCatalog, copy } from './kit-catalog.js';
import { createStore } from './kit-store.js';
import { createStage } from './kit-stage.js';
import { createPrintSurface } from './kit-print.js';
import { createCart } from './kit-cart.js';
import { createUI } from './kit-ui.js';
import { VIEWPOINTS } from './kit-catalog.js';
import { applyConfig, readEditParams } from './kit-config.js';

/**
 * A WebGL context is not guaranteed (old GPU, locked-down browser, headless
 * crawler) and `createStage` throws without one. Uncaught, the section sits on
 * the theme's loading placeholder forever with nothing to explain it, because
 * the status element is only rewritten by code that never ran.
 */
function boot() {
  const mount = document.querySelector('[data-kit-studio]');
  if (!mount) return;
  try {
    start(mount);
  } catch (error) {
    console.error('[kit-studio] could not start', error);
    const status = mount.querySelector('[data-kit="status"]');
    if (status) {
      status.hidden = false;
      status.textContent = copy.previewUnavailable;
    }
  }
}

function start(mount) {
  const settings = window.KitStudioSettings || {};
  const catalog = buildCatalog(settings);
  const store = createStore(catalog);

  /**
   * Reopening a design from the cart. `?kit=` carries the configuration and
   * `?line=` the cart line it came from, so a re-add replaces the original
   * instead of leaving the shopper with two nearly identical lines.
   *
   * Applied before anything subscribes, so the first garment load already has
   * the right garment and the stage never visibly swaps under the shopper.
   */
  const edit = readEditParams(catalog);
  if (edit.config) applyConfig(store, catalog, edit.config);

  /**
   * `?garment=` is a deep link: the navbar and the button on each product page.
   *
   * After applyConfig on purpose. An explicit `?garment=` is what the shopper
   * clicked a moment ago; the garment inside a `?kit=` payload is whatever they
   * last saved, which can be weeks old. The more recent intent wins. Both run
   * before the first load below, so this costs no visible swap either.
   */
  if (edit.garment) store.actions.chooseGarment(edit.garment);

  const viewport = mount.querySelector('[data-kit="viewport"]');
  const canvas = mount.querySelector('[data-kit="canvas"]');
  const stage = createStage({ canvas, viewport, transcoderPath: settings.transcoderPath });
  const prints = createPrintSurface(stage);
  const cart = createCart({ settings, catalog, store });
  const ui = createUI({ mount, catalog, store, cart });

  /* --- garment loading ----------------------------------------------------- */

  let loadedGarmentId = null;

  async function loadGarment(first = false) {
    const garment = store.select.garment();
    store.actions.setStatus('loading', garment.name);
    prints.detach();

    try {
      const { primary } = await stage.load(garment.url, garment.fit);
      loadedGarmentId = garment.id;

      stage.tint(store.select.colourway().fabric);
      // Re-aim every zone at the new mesh, then re-print what was already
      // configured. A garment swap must never lose the shopper's design.
      prints.attach(garment.zones, primary);
      applyAllZones(garment);

      store.actions.setStatus('ready');
      const viewpoint = VIEWPOINTS[store.state.viewpoint] || VIEWPOINTS.front;
      if (first) stage.snapTo(viewpoint);
      else stage.glideToward(viewpoint);
      if (first) warmOtherGarments();
    } catch (error) {
      console.warn('[kit-studio] could not load', garment.url, error);
      store.actions.setStatus('error', garment.name);
    }
  }

  /**
   * Build the other garments once the first is on screen, so the first switch
   * costs a frame rather than a download plus a parse plus a transcode.
   *
   * Full parses, not just bytes: a warm HTTP cache removes the download but
   * leaves the transcode and GPU upload, which is most of what the shopper
   * feels. Sequential, because three parallel Basis transcodes saturate the
   * worker pool and stutter the garment already on screen.
   */
  let warmed = false;
  async function warmOtherGarments() {
    if (warmed || !shouldPrewarm()) return;
    warmed = true;
    const current = store.select.garment().id;
    for (const garment of catalog.garments) {
      if (garment.id === current) continue;
      const built = await stage.preload(garment.url, garment.fit);
      if (!built) continue;
      // Aiming costs more than parsing does. See the note on `anchors` in
      // kit-print.js. So a garment warmed without its anchors is still a
      // fifth-of-a-second switch. Do both or the prewarm barely pays.
      //
      // It is also the one genuinely blocking step: the ray fan runs to
      // completion on the main thread and measured ~450ms on the hoodie. Held
      // until the browser is idle so it lands between the shopper's frames
      // rather than through the middle of a drag.
      await whenIdle();
      prints.warm(built.root, garment.zones, built.primary);
    }
  }

  /**
   * Resolve when the browser has nothing better to do, or after `timeout`
   * regardless. The shopper who never stops interacting must still end up
   * with a warm catalog, just later. Falls back to a plain task on Safari,
   * which still has no requestIdleCallback.
   */
  function whenIdle(timeout = 2000) {
    return new Promise((resolve) => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(resolve, { timeout });
      else setTimeout(resolve, 0);
    });
  }

  /**
   * Hold back on the devices that can least afford it. Prewarming trades
   * bandwidth and VRAM for latency, and on a metered connection or a 2 GB
   * phone that trade goes the wrong way. Those are also the devices where
   * three resident garments risk a context loss rather than a slow switch.
   */
  function shouldPrewarm() {
    if (settings.prewarm === false) return false;
    if (navigator.connection?.saveData) return false;
    if (navigator.deviceMemory && navigator.deviceMemory < 4) return false;
    return true;
  }

  /**
   * Return to the ordinary "build one" state once an edit has been saved.
   *
   * Without this the shopper is stranded: the URL still carries `?kit=&line=`
   * and the button still reads "Save changes", so nothing on screen says the
   * edit is finished. Someone configuring a second, different garment reads
   * that button as the way to add it, and every visual cue agrees with them.
   *
   * Deliberately not a page reload. The design stays on screen, which is a
   * sensible starting point for a second item, and reloading would throw away
   * the loaded garment and make them wait again for no benefit. What changes is
   * the part that was lying: the URL and the button.
   */
  function leaveEditMode() {
    edit.line = null;
    edit.config = null;
    edit.rawConfig = null;
    ui.setEditing(false);
    // Drop the edit parameters so a refresh, a share or a back-button press
    // doesn't drop them back into editing a line that no longer exists.
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  /** Push every zone's current selection onto the freshly attached garment. */
  function applyAllZones(garment) {
    for (const zone of garment.zones) applyZone(zone);
  }

  function applyZone(zone) {
    const selection = store.state.zones[zone.id];
    // 'fixed' zones carry artwork too. They just can't be changed. Routing
    // them down the lettering branch would silently drop the Canada Games logo.
    if (zone.kind === 'lettering') {
      prints.setLettering(zone, selection.text, store.select.inkHex(zone.id));
    } else {
      // Through the selector, not `selection.artworkId`: the front logo's
      // artwork depends on the colour, and only the selector knows that.
      const id = store.select.artworkId(zone.id);
      const item = zone.library.find((a) => a.id === id);
      prints.setArtwork(zone, item ? item.src : null);
    }
  }

  /* --- reactions ----------------------------------------------------------- */

  let previousZones = store.state.zones;

  store.subscribe((state, keys) => {
    if (keys.includes('garmentId') && state.garmentId !== loadedGarmentId) {
      loadGarment();
      previousZones = state.zones;
      return;
    }

    if (keys.includes('colourwayId')) {
      stage.tint(store.select.colourway().fabric);
      // Brand rules pick the front logo by colour: white on navy, full colour
      // with dark text on sand. So a colour change is a reprint as well as a
      // tint, for exactly the zones whose artwork follows the colour.
      for (const zone of store.select.garment().zones) {
        if (zone.artworkByColour || zone.inkByColour) applyZone(zone);
      }
    }

    if (keys.includes('viewpoint')) {
      const viewpoint = VIEWPOINTS[state.viewpoint];
      if (viewpoint) stage.glideToward(viewpoint);
    }

    if (keys.includes('zones')) {
      // Reprint only the zone that actually changed. Rebuilding all four on
      // every keystroke would re-project four decals per character typed.
      const garment = store.select.garment();
      for (const zone of garment.zones) {
        if (previousZones[zone.id] !== state.zones[zone.id]) applyZone(zone);
      }
      previousZones = state.zones;
    }
  });

  /* --- the 360 button ------------------------------------------------------ */

  /**
   * Wired here rather than in kit-ui.js because it is the one control that acts
   * on the stage and on nothing else. It changes no configuration, so it has no
   * business going through the store: a turn is a look at the garment, not a
   * decision about it, and a state key for it would be one more thing every
   * subscriber has to ignore.
   */
  const spinButton = mount.querySelector('[data-kit="spin"]');
  spinButton?.addEventListener('click', () => {
    spinButton.classList.add('is-spinning');
    // Settles when the turn lands and also when a drag or a view button cuts it
    // short, so the button can never be left lit over a garment standing still.
    stage.spin().then(() => spinButton.classList.remove('is-spinning'));
  });

  /* --- add to cart --------------------------------------------------------- */

  mount.querySelector('[data-kit="add-to-cart"]')?.addEventListener('click', async () => {
    if (store.state.cartState === 'busy') return;
    store.actions.setCartState('busy');
    try {
      const result = await cart.add();
      if (!result.ok) {
        const message =
          result.reason === 'no-variant' || result.reason === 'sold-out'
            ? catalog.copy.unavailableCombo
            : catalog.copy.cartFailed;
        store.actions.setCartState('idle');
        window.alert(message);
        return;
      }
      // Add first, then drop the original. The other order risks a failed add
      // leaving the shopper with nothing in the cart at all.
      /* Whether there is still a line to drop.
         Normally there is NOT: kit-cart-edit.js removes it the moment the
         shopper clicks Edit, while it is standing in the cart holding a key
         that is seconds old, and then sends them here with a plain `?kit=`.
         Save is an ordinary add and this whole block is skipped.

         `?line=` only survives when that removal failed, or when the link was
         followed without JavaScript. Then it is still on us, and the same key
         is by now old enough to be worth the fallback in `removeLine`. */
      const wasEdit = !!edit.line;
      if (wasEdit) {
        const dropped = await cart.removeLine(edit.line, edit.rawConfig);
        // Not swallowed. A failure here is what puts two nearly identical lines
        // in the cart, one at the old price and one at the new, and it used to
        // do exactly that in silence: the return value was discarded, so the
        // only place it ever showed up was the shopper's cart.
        if (!dropped) {
          console.warn(
            '[kit-studio] saved the edit but could not remove the original cart line.',
            'The shopper now has both. line=', edit.line
          );
        }
        edit.line = null;
        edit.rawConfig = null;
      }
      store.actions.setCartState('done');
      // The theme's drawer or popup is already showing the line; going to the
      // cart page as well would pull the page out from under it.
      if (cart.isLive && !result.shownByTheme && settings.redirectToCart !== false) {
        window.location.href = settings.cartUrl || '/cart';
        return;
      }
      setTimeout(() => {
        store.actions.setCartState('idle');
        // After the confirmation, not before, or the button reads "Added"
        // instead of "Saved" at the one moment the shopper is looking at it.
        if (wasEdit) leaveEditMode();
      }, 1600);
    } catch (error) {
      console.error('[kit-studio] add to cart failed', error);
      store.actions.setCartState('idle');
      window.alert(catalog.copy.cartFailed);
    }
  });

  /* --- go ------------------------------------------------------------------ */

  if (edit.config) ui.setEditing(true);
  ui.mount();
  stage.start();
  loadGarment(true);

  // Handy from the console and from theme-editor smoke tests; also the seam a
  // future bilingual pass or an analytics hook can attach to.
  window.KitStudio = { catalog, store, stage, prints, cart, reload: () => loadGarment(true) };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
